import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { 
  marcarNotificacaoComoLida,
  getNaoLidas 
} from '../services/notificacaoService';
import { useAuth } from '../hooks/useAuth';


export const NotificacaoContext = createContext(null);

export const NotificacaoProvider = ({ children }) => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchNotificacoes = useCallback(async () => {
    if (user) {
      try {
        setLoading(true);
        console.log('Buscando notificações...');

        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('Token não encontrado no localStorage.');
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/notificacoes`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('Resposta das notificações:', response.data);
        setNotificacoes(response.data);

        // Buscar contagem específica de não lidas
        const naoLidasResponse = await getNaoLidas();
        setNaoLidas(naoLidasResponse.count);

      } catch (error) {
        console.error('Erro ao buscar notificações:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  const atualizarContadorNaoLidas = useCallback(async () => {
    if (user) {
      try {
        // Usa o getNaoLidas para manter consistência com o servidor
        const response = await getNaoLidas();
        setNaoLidas(response.count);
      } catch (error) {
        console.error('Erro ao atualizar contador de não lidas:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchNotificacoes();
    const interval = setInterval(atualizarContadorNaoLidas, 300000);
    return () => clearInterval(interval);
  }, [fetchNotificacoes, atualizarContadorNaoLidas]);

  const removerNotificacao = useCallback((id) => {
    setNotificacoes((prev) => prev.filter((notificacao) => notificacao.id !== id));
    atualizarContadorNaoLidas();
  }, [atualizarContadorNaoLidas]);

  const adicionarNotificacao = useCallback((titulo, mensagem, tipo = 'info') => {
    const novaNotificacao = {
      id: uuidv4(),
      titulo,
      mensagem,
      tipo,
      lida: false,
      createdAt: new Date().toISOString()
    };
    setNotificacoes((prev) => [novaNotificacao, ...prev]);
    setNaoLidas((prev) => prev + 1);
  }, []);

  const marcarComoLida = useCallback(async (id) => {
    try {
      await marcarNotificacaoComoLida(id);
      setNotificacoes((prev) =>
        prev.map((notificacao) =>
          notificacao.id === id ? { ...notificacao, lida: true } : notificacao
        )
      );
      atualizarContadorNaoLidas();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  }, [atualizarContadorNaoLidas]);

  const value = {
    notificacoes,
    loading,
    naoLidas,
    adicionarNotificacao,
    removerNotificacao,
    marcarComoLida,
    fetchNotificacoes,
    atualizarContadorNaoLidas
  };

  return (
    <NotificacaoContext.Provider value={value}>
      {children}
    </NotificacaoContext.Provider>
  );
};

NotificacaoProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNotificacao = () => {
  const context = useContext(NotificacaoContext);
  if (!context) {
    throw new Error('useNotificacao deve ser usado dentro de um NotificacaoProvider');
  }
  return context;
};

export default NotificacaoProvider;